// Pure helpers for block change requests. Field lists mirror lox_api BlockOperationNormalizer.
import { hasUserPermission } from '../../../utils/permissionHelper'
import { buildUrlState, treatmentPath } from './blocksExplorerHelpers'

const STATUSES = {
  draft: {
    label: 'Draft',
    tagClass: 'is-light',
    isEditable: true,
    isOpen: true,
  },
  submitted: {
    label: 'Submitted',
    tagClass: 'is-info',
    isEditable: false,
    isOpen: true,
  },
  changes_requested: {
    label: 'Changes requested',
    tagClass: 'is-warning',
    isEditable: true,
    isOpen: true,
  },
  applied: {
    label: 'Applied',
    tagClass: 'is-success',
    isEditable: false,
    isOpen: false,
  },
  rejected: {
    label: 'Rejected',
    tagClass: 'is-danger',
    isEditable: false,
    isOpen: false,
  },
  discarded: {
    label: 'Discarded',
    tagClass: 'is-dark',
    isEditable: false,
    isOpen: false,
  },
}

export const CHANGE_REQUEST_STATUSES = Object.keys(STATUSES)

export const OP_LABELS = {
  'block.create': 'New block',
  'block.update': 'Edit',
  'block.override': 'Override',
  'block.deactivate': 'Deactivate',
  'block.activate': 'Activate',
}

export function statusMeta(status) {
  return (
    STATUSES[status] ?? {
      label: status,
      tagClass: 'is-light',
      isEditable: false,
      isOpen: false,
    }
  )
}

export function blockPermissions(activePermissions) {
  const has = (verb) =>
    hasUserPermission(activePermissions, `lead_funnel_block_${verb}`)
  return {
    canRead: has('read'),
    canCreate: has('create'),
    canUpdate: has('update'),
    canDelete: has('delete'),
    canApprove: has('approve'),
  }
}

// A row the draft creates has the id -N for its temp:N until it is applied.
export function operationTarget({ table, id }) {
  return id < 0
    ? { target_table: table, target_temp_ref: `temp:${-id}` }
    : { target_table: table, target_id: id }
}

const CREATE_FIELDS = {
  numeric: [
    'name',
    'attribute',
    'customer_type',
    'treatment_id',
    'block_action',
    'min',
    'max',
    'rejection_reason',
    'adverse_action_score_key',
    'pre_uw',
    'post_buy',
  ],
  categorical: [
    'name',
    'customer_type',
    'treatment_id',
    'block_action',
    'rejection_reason',
    'conditions',
    'is_compliance',
    'post_buy',
    'type',
  ],
}
const OVERRIDE_FIELDS = [
  'treatment_id',
  'block_action',
  'customer_type',
  'rejection_reason',
  'name',
]
const NUMERIC_OVERRIDE_EXTRA_FIELDS = [
  'min',
  'max',
  'attribute',
  'adverse_action_score_key',
]

function overrideFields(table) {
  return table === 'numeric'
    ? [...OVERRIDE_FIELDS, ...NUMERIC_OVERRIDE_EXTRA_FIELDS]
    : OVERRIDE_FIELDS
}

// An edit of a draft row is merged into the op that made it, so an override's row only takes override fields.
export function editableFields({ op, table, createdByOp = null }) {
  if (op === 'block.create') return CREATE_FIELDS[table]
  if (op === 'block.update') {
    const fields =
      createdByOp === 'block.override'
        ? overrideFields(table)
        : CREATE_FIELDS[table]
    return fields.filter((field) => field !== 'treatment_id')
  }
  if (op === 'block.override') return overrideFields(table)
  return []
}

export const FIELD_TYPES = {
  min: 'float',
  max: 'float',
  treatment_id: 'int',
  block_action: 'int',
  pre_uw: 'int',
  post_buy: 'int',
  is_compliance: 'int',
  conditions: 'conditions',
}

// Mirrors the API's limits on an op's `after`, so a value it would refuse is caught in the form.
const TEXT_MAX = 16383
const MAX_LENGTHS = {
  name: TEXT_MAX,
  attribute: TEXT_MAX,
  customer_type: TEXT_MAX,
  rejection_reason: 255,
  adverse_action_score_key: 10,
  type: 255,
}
const CONDITION_MAX_LENGTH = 255
const TINYINT_RANGE = [-128, 127]
const INT_RANGE = [-2147483648, 2147483647]
const INT_RANGES = {
  treatment_id: INT_RANGE,
  block_action: TINYINT_RANGE,
  pre_uw: INT_RANGE,
  post_buy: TINYINT_RANGE,
  is_compliance: INT_RANGE,
}

// Counts characters the way the API does (mb_strlen), not UTF-16 code units.
const lengthOf = (text) => [...text].length
const tooLong = (max) => `Must be at most ${max} characters`

function parseConditions(raw) {
  const conditions = []
  let error = null
  const rows = raw ?? []
  rows.forEach((condition, index) => {
    const parsed = {
      field_name: String(condition.field_name ?? '').trim(),
      field_value: String(condition.field_value ?? '').trim(),
    }
    if (parsed.field_name === '') {
      if (parsed.field_value !== '' && !error) {
        error = `Condition ${index + 1}: field name is required`
      }
      return
    }
    const longPart = ['field_name', 'field_value'].find(
      (part) => lengthOf(parsed[part]) > CONDITION_MAX_LENGTH
    )
    if (longPart && !error) {
      const label = longPart === 'field_name' ? 'field name' : 'value'
      error = `Condition ${
        index + 1
      }: ${label} must be at most ${CONDITION_MAX_LENGTH} characters`
    }
    conditions.push(parsed)
  })
  return error ? { error } : { value: conditions }
}

// Text is trimmed as the API's TrimStrings middleware does, so padding alone is no change and does not count to a limit.
function parseField(field, raw) {
  const type = FIELD_TYPES[field] ?? 'text'
  if (type === 'conditions') return parseConditions(raw)
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return { value: null }
  }
  if (type === 'text') {
    const text = String(raw).trim()
    const max = MAX_LENGTHS[field]
    if (max !== undefined && lengthOf(text) > max) {
      return { error: tooLong(max) }
    }
    return { value: text }
  }
  const number = Number(raw)
  // Infinity would serialise to null and silently clear the field.
  if (!Number.isFinite(number)) return { error: 'Must be a number' }
  if (type === 'int' && !Number.isInteger(number)) {
    return { error: 'Must be a whole number' }
  }
  const range = INT_RANGES[field]
  if (range && (number < range[0] || number > range[1])) {
    return { error: `Must be between ${range[0]} and ${range[1]}` }
  }
  return { value: number }
}

const AFTER_PREFIX = /\bafter\.(?=\w)/g

// Laravel names an op's fields `after.<field>`; the form knows them as `<field>`.
export function withoutAfterPrefix(message) {
  return typeof message === 'string'
    ? message.replace(AFTER_PREFIX, '')
    : message
}

// Maps a 422's errors bag onto the form's fields; `after.conditions.N.*` lands on `conditions`.
export function fieldErrorsFromValidation(errors) {
  const fieldErrors = {}
  Object.entries(errors ?? {}).forEach(([key, messages]) => {
    const match = /^after\.([a-z_]+)/.exec(key)
    if (!match || fieldErrors[match[1]]) return
    fieldErrors[match[1]] = withoutAfterPrefix([].concat(messages)[0])
  })
  return fieldErrors
}

function sameValue(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

// Builds an op's `after`: a create sends what is filled in; an edit sends only what changed.
export function buildAfter({
  op,
  table,
  row,
  values,
  currentConditions = [],
  createdByOp = null,
}) {
  const after = {}
  const errors = {}

  editableFields({ op, table, createdByOp }).forEach((field) => {
    if (!(field in values)) return
    const { value, error } = parseField(field, values[field])
    if (error) {
      errors[field] = error
      return
    }
    if (op === 'block.create') {
      const isEmpty =
        value === null || (Array.isArray(value) && value.length === 0)
      if (!isEmpty) after[field] = value
      return
    }
    const current = field === 'conditions' ? currentConditions : row?.[field]
    // Without a row to compare with (an override aimed past an override at its original), every field is sent.
    const alwaysSent =
      op === 'block.override' && (field === 'treatment_id' || !row)
    if (alwaysSent || !sameValue(value, current)) after[field] = value
  })

  return { after, errors }
}

const SUMMARY_FIELD_LABELS = {
  block_action: 'action',
  customer_type: 'customer',
}
// The tray row shows the treatment, and the op label already says when a row is turned on or off.
const SUMMARY_HIDDEN_FIELDS = ['treatment_id', 'active']
const SUMMARY_LIMIT = 2

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

export function diffCardSummary({ card, actionLabels }) {
  function show(field, value) {
    if (field !== 'block_action') return String(value)
    return actionLabels[value] ?? `Action ${value}`
  }
  const parts = card.field_changes
    .filter((change) => !SUMMARY_HIDDEN_FIELDS.includes(change.field))
    .map(({ field, before, after }) => {
      const label = SUMMARY_FIELD_LABELS[field] ?? field
      if (before === null || before === undefined) {
        return `${label} ${show(field, after)}`
      }
      return `${label} ${show(field, before)} → ${show(field, after)}`
    })
  if (card.conditions_added.length > 0) {
    parts.push(`+${plural(card.conditions_added.length, 'condition')}`)
  }
  if (card.conditions_removed.length > 0) {
    parts.push(`−${plural(card.conditions_removed.length, 'condition')}`)
  }
  const shown = parts.slice(0, SUMMARY_LIMIT)
  if (parts.length > SUMMARY_LIMIT) {
    shown.push(`+${parts.length - SUMMARY_LIMIT} more`)
  }
  return shown.join(', ')
}

export function diffCardTreatment({ card, treatmentNamesById }) {
  const nameOf = (id) => treatmentNamesById[id] ?? `treatment ${id}`
  if (card.op === 'block.override') {
    const move = card.field_changes.find(
      (change) => change.field === 'treatment_id'
    )
    if (move && move.before !== null && move.before !== undefined) {
      return `${nameOf(move.before)} → ${nameOf(move.after)}`
    }
  }
  if (card.treatment_id === null || card.treatment_id === undefined) {
    return null
  }
  return `on ${nameOf(card.treatment_id)}`
}

// A create or override is opened at the row it makes; anything else at the row it changes.
export function diffCardBlock(card) {
  const tempRef = card.temp_ref ?? card.target_temp_ref
  if (card.temp_ref || !card.target_id) {
    // A card naming no row at all has nothing to link to.
    if (!tempRef) return null
    return { table: card.table, id: -Number(tempRef.slice('temp:'.length)) }
  }
  return { table: card.table, id: card.target_id }
}

const tempRefId = (tempRef) => -Number(tempRef.slice('temp:'.length))

// The op that made a draft row, from that row's own cards; null for a live row.
export function creatingOp({ cards, id }) {
  if (id >= 0) return null
  return cards.find((card) => card.temp_ref)?.op ?? null
}

// A row's changes: those aimed at it, and for a draft row the change that made it.
export function cardsForBlock({ cards, table, id }) {
  return cards.filter(
    (card) =>
      card.table === table &&
      ((id > 0 && card.target_id === id) ||
        (card.temp_ref && tempRefId(card.temp_ref) === id) ||
        (card.target_temp_ref && tempRefId(card.target_temp_ref) === id))
  )
}

function treatmentOrder(nodes, order = []) {
  nodes.forEach((node) => {
    order.push(node.id)
    treatmentOrder(node.children, order)
  })
  return order
}

function treatmentLabel(nodes, treatmentId) {
  if (treatmentId === null || treatmentId === undefined) {
    return 'Unknown treatment'
  }
  const path = treatmentPath(nodes, treatmentId)
  if (path.length === 0) return `Treatment ${treatmentId}`
  return path.map((node) => node.name).join(' › ')
}

// Groups follow the tree, so a parent's changes come before its children's; ids the tree lacks go last.
export function groupCardsByTreatment({ cards, nodes }) {
  const order = treatmentOrder(nodes)
  const rank = (treatmentId) => {
    if (treatmentId === null || treatmentId === undefined) {
      return Number.MAX_SAFE_INTEGER
    }
    const index = order.indexOf(treatmentId)
    return index === -1 ? order.length : index
  }
  const groups = new Map()
  cards.forEach((card) => {
    const treatmentId = card.treatment_id ?? null
    if (!groups.has(treatmentId)) groups.set(treatmentId, [])
    groups.get(treatmentId).push(card)
  })
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || (a ?? 0) - (b ?? 0))
    .map(([treatmentId, groupCards]) => ({
      treatmentId,
      label: treatmentLabel(nodes, treatmentId),
      cards: groupCards,
    }))
}

const SCOPED_CUSTOMER_TYPES = ['NEW', 'REACT']

// A NEW- or REACT-only row is cut from the other type's list; an op that does not touch the field keeps the row's own.
function linkCustomerType({ card, operation, preferBefore }) {
  const change = (card.field_changes ?? []).find(
    ({ field }) => field === 'customer_type'
  )
  const after = operation?.after?.customer_type ?? change?.after
  const before = operation?.before?.customer_type ?? change?.before
  const type =
    (preferBefore ? before ?? after : after ?? before) ?? card.customer_type
  return SCOPED_CUSTOMER_TYPES.includes(type) ? type : 'NEW'
}

// An open request is viewed as its draft; a closed one at the live row, which is the applied result when there is one.
export function reviewBlockLink({ card, operation, changeRequest }) {
  const isOpen = statusMeta(changeRequest.status).isOpen
  let block = null
  if (isOpen) {
    block = diffCardBlock(card)
  } else {
    const liveId =
      changeRequest.status === 'applied'
        ? operation?.result_id ?? card.target_id
        : card.target_id
    if (liveId) block = { table: card.table, id: liveId }
  }
  if (!block) return null
  const params = buildUrlState({
    treatmentId: card.treatment_id,
    customerType: linkCustomerType({
      card,
      operation,
      preferBefore: !isOpen && changeRequest.status !== 'applied',
    }),
    block,
    changeRequestId: isOpen ? changeRequest.id : null,
  })
  return `/lead_funnel_blocks?${new URLSearchParams(params).toString()}`
}

export function findingsForOperation({ validation, sequence }) {
  const ofOperation = (finding) => finding.operation_sequence === sequence
  return {
    errors: validation.errors.filter(ofOperation),
    warnings: validation.warnings.filter(ofOperation),
  }
}

export function isOverrideRow(row) {
  return (
    row.ancestor_id !== null &&
    row.ancestor_id !== undefined &&
    row.ancestor_id !== row.id
  )
}

// What a row offers in the drawer, judged from its lineage: the last level is the treatment being viewed.
export function rowActions({ detail }) {
  const { row, lineage } = detail
  const here = lineage[lineage.length - 1]
  const offer = (actions, override = {}) => ({
    actions,
    overrideTreatmentId: null,
    overrideTargetId: null,
    ...override,
  })
  if (row.active !== 1) {
    // Levels only hold active rows, so an inactive row is placed by its own treatment.
    const isOnLineage = lineage.some(
      (level) => level.treatment_id === row.treatment_id
    )
    return offer(isOnLineage ? ['block.activate'] : [])
  }
  if (row.id < 0) {
    // A draft row is edited where it lands; below that it is only inherited.
    return offer(
      here?.treatment_id === row.treatment_id ? ['block.update'] : []
    )
  }

  if (here?.row?.id === row.id) {
    return offer(['block.update', 'block.deactivate'])
  }
  // A row on no level of this treatment's lineage is not inherited here, so there is nothing to override.
  if (!lineage.some((level) => level.row?.id === row.id)) {
    return offer([])
  }
  // An override of an override is flagged by the API, so a new one always replaces the original.
  return offer(['block.override'], {
    overrideTreatmentId: here?.treatment_id ?? null,
    overrideTargetId: isOverrideRow(row) ? row.ancestor_id : row.id,
  })
}

export function reviewActions({ changeRequest, currentUserId, permissions }) {
  const meta = statusMeta(changeRequest.status)
  const isAuthor = changeRequest.created_by_user_id === currentUserId
  const actions = []

  if (meta.isEditable && isAuthor && permissions.canCreate) {
    actions.push('edit', 'submit')
    if (permissions.canDelete) actions.push('discard')
  }
  if (changeRequest.status === 'submitted') {
    if (permissions.canUpdate) actions.push('request_changes', 'reject')
    if (permissions.canApprove) actions.push('approve')
  }
  if (
    changeRequest.status === 'applied' &&
    !changeRequest.cache_cleared_at &&
    permissions.canApprove
  ) {
    actions.push('retry_cache_clear')
  }
  if (permissions.canUpdate) actions.push('comment')

  return actions
}

export function approveReadiness({ validation, acknowledged, comment }) {
  if (validation.errors.length > 0) {
    return { ready: false, reason: 'Fix the errors before approving.' }
  }
  const warningCount = validation.warnings.length
  if (warningCount > 0 && !acknowledged) {
    return {
      ready: false,
      reason: `Acknowledge the ${warningCount} warning${
        warningCount === 1 ? '' : 's'
      }.`,
    }
  }
  if ((comment ?? '').trim() === '') {
    return { ready: false, reason: 'A comment is required.' }
  }
  return { ready: true, reason: null }
}

// Only counts and max ids are recorded per apply, so that is all the banner can say about what moved.
export function driftSummary(checksum) {
  if (!checksum || checksum.in_sync) return null
  if (!checksum.last_applied) {
    return {
      message:
        'No baseline has been recorded yet; run lead-funnel:block-baseline.',
      changes: [],
    }
  }

  const before = checksum.last_applied_table_stats ?? {}
  const now = checksum.current_table_stats ?? {}
  const changes = Object.keys(now)
    .filter((table) => before[table] && !sameValue(before[table], now[table]))
    .map(
      (table) =>
        `${table}: ${before[table].count} → ${now[table].count} rows, max id ${before[table].max_id} → ${now[table].max_id}`
    )

  return {
    message: `Block tables changed outside this tool since CR-${checksum.last_applied_change_request_id} (applied ${checksum.last_applied_at}).`,
    changes,
  }
}

// Unsent form input survives a reload; the server draft stays the source of truth. Storage may be blocked.
// The target treatment is in the key so input started on one treatment never restores on another.
const formDraftKey = ({ changeRequestId, treatmentId, table, id, op }) =>
  `lox.blockChange.form.${changeRequestId}.${treatmentId}.${table}.${id}.${op}`

// A field is restored only while the row still holds the value it was edited from, so a newer saved change wins.
export function readFormDraft(key, initialValues = {}) {
  let stored = null
  try {
    stored = JSON.parse(window.localStorage.getItem(formDraftKey(key)))
  } catch (error) {
    return null
  }
  if (!stored?.base || !stored?.values || typeof stored.values !== 'object') {
    return null
  }
  const isStillBase = (field) =>
    sameValue(stored.base[field], initialValues[field])
  const restored = Object.keys(stored.values).filter(isStillBase)
  if (restored.length === 0) return null
  return {
    ...initialValues,
    ...Object.fromEntries(
      restored.map((field) => [field, stored.values[field]])
    ),
  }
}

// Only the fields the user changed are kept, each with the value it was changed from.
export function writeFormDraft({ values, initialValues = {}, ...key }) {
  const edited = Object.keys(values).filter(
    (field) => !sameValue(values[field], initialValues[field])
  )
  const pick = (source) =>
    Object.fromEntries(edited.map((field) => [field, source[field] ?? null]))
  try {
    if (edited.length === 0) {
      window.localStorage.removeItem(formDraftKey(key))
      return
    }
    window.localStorage.setItem(
      formDraftKey(key),
      JSON.stringify({ base: pick(initialValues), values: pick(values) })
    )
  } catch (error) {
    // Storage is a convenience here; losing it only loses unsent input.
  }
}

// Unsent input for a draft row goes with the change that made it, so a reused temp id starts empty.
export function clearRowFormDrafts({ changeRequestId, table, id }) {
  const prefix = `lox.blockChange.form.${changeRequestId}.`
  // The rest of the key is <treatment>.<table>.<id>.<op>, and any treatment or op is cleared.
  const isRowKey = (key) => {
    if (!key?.startsWith(prefix)) return false
    const [, keyTable, keyId] = key.slice(prefix.length).split('.')
    return keyTable === table && keyId === String(id)
  }
  try {
    const keys = Array.from(
      { length: window.localStorage.length },
      (_, index) => window.localStorage.key(index)
    )
    keys.filter(isRowKey).forEach((key) => window.localStorage.removeItem(key))
  } catch (error) {
    // See writeFormDraft.
  }
}

export function clearFormDraft(key) {
  try {
    window.localStorage.removeItem(formDraftKey(key))
  } catch (error) {
    // See writeFormDraft.
  }
}
